import React, { Component, ErrorInfo, ReactNode } from 'react'

interface Props {
  children: ReactNode
  fallback?: ReactNode
  onError?: (error: Error, errorInfo: ErrorInfo) => void
}

interface State {
  hasError: boolean
  error?: Error
  errorInfo?: ErrorInfo
  timestamp?: string
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
  }

  public static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      timestamp: new Date().toISOString(),
    }
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Unhandled application error:', error, errorInfo)
    this.setState({ errorInfo })

    this.props.onError?.(error, errorInfo)
  }

  private handleReload = () => {
    window.location.reload()
  }

  private handleGoBack = () => {
    window.history.back()
    this.setState({ hasError: false })
      setTimeout(() => {
    window.location.reload()
       this.setState({ hasError: false })
  }, 100)
  }

  public render() {
    const { hasError, error, errorInfo, timestamp } = this.state

    if (hasError) {
      return (
        <div className="w-full min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900 px-4">
          <div className="w-full max-w-md rounded-xl bg-white dark:bg-slate-800 shadow-lg p-8 text-center">
            <div className="text-5xl mb-4">😕</div>

            <h1 className="text-lg font-semibold text-slate-800 dark:text-slate-100 mb-2">
              This page couldn’t load properly
            </h1>

            <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">
              Don’t worry — this isn’t your fault.  
              Please refresh the page or try again in a moment.
            </p>

            <div className="flex justify-center gap-3">
              <button
                onClick={this.handleReload}
                className="px-4 py-2 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition"
              >
                Refresh page
              </button>

              <button
                onClick={this.handleGoBack}
                className="px-4 py-2 rounded-md bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 text-sm font-medium hover:bg-slate-300 dark:hover:bg-slate-600 transition"
              >
                Go back
              </button>
            </div>

            {/* ================= DEV ONLY DETAILS ================= */}
            {process.env.NODE_ENV === 'development' && error && (
              <details className="mt-6 text-left text-xs text-slate-500 dark:text-slate-400">
                <summary className="cursor-pointer select-none font-medium">
                  Technical details (development only)
                </summary>

                <div className="mt-3 space-y-2">
                  <div>
                    <span className="font-semibold">Error:</span>{' '}
                    {error.name}: {error.message}
                  </div>

                  {timestamp && (
                    <div>
                      <span className="font-semibold">Time:</span>{' '}
                      {timestamp}
                    </div>
                  )}

                  <div>
                    <span className="font-semibold">User Agent:</span>{' '}
                    {navigator.userAgent}
                  </div>

                  {errorInfo?.componentStack && (
                    <div>
                      <span className="font-semibold">Component stack:</span>
                      <pre className="mt-1 whitespace-pre-wrap break-words bg-slate-100 dark:bg-slate-900 p-2 rounded">
                        {errorInfo.componentStack.trim()}
                      </pre>
                    </div>
                  )}

                  {error.stack && (
                    <div>
                      <span className="font-semibold">Stack trace:</span>
                      <pre className="mt-1 whitespace-pre-wrap break-words bg-slate-100 dark:bg-slate-900 p-2 rounded">
                        {error.stack}
                      </pre>
                    </div>
                  )}
                </div>
              </details>
            )}
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

export default ErrorBoundary
